import { BENEFITS_CONVERSATIONS } from "./constants";
import {
  clearBenefitsSession,
  getBenefitsAccessToken,
  isBenefitsGuestSession,
  isBenefitsConversationId,
  refreshBenefitsAccessToken,
} from "./benefitsSession";
import { getGuestId, storeGuestIdFromHeaders } from "./guestSession";

export type BenefitsConversationSummary = {
  conversationId: string;
  title: string;
  createdAt: string;
  updatedAt: string;
  lastMessage: string;
};

export type BenefitsConversationMessage = {
  id: string;
  role: "user" | "assistant";
  content: string;
  createdAt: string;
};

export type BenefitsConversationThread = {
  conversationId: string;
  title: string;
  createdAt: string;
  updatedAt: string;
  messages: BenefitsConversationMessage[];
};

export type BenefitsConversationPage = {
  conversations: BenefitsConversationSummary[];
  nextCursor: string | null;
};

export const DEFAULT_BENEFITS_CONVERSATION_TITLE = "Benefits chat";

export class BenefitsAuthenticationError extends Error {}
export class BenefitsConversationNotFoundError extends Error {}

const unwrapPayload = (payload: any) => payload?.data ?? payload;

export const benefitsAuthenticatedFetch = async (
  input: RequestInfo | URL,
  init: RequestInit = {}
) => {
  let benefitsAccessToken = getBenefitsAccessToken();
  const accountAccessToken = localStorage.getItem("token");
  let hasRefreshed = false;
  const isGuestSession = isBenefitsGuestSession();

  if (!benefitsAccessToken && !accountAccessToken && !isGuestSession) {
    benefitsAccessToken = await refreshBenefitsAccessToken();
    hasRefreshed = true;
  }

  if (!benefitsAccessToken && !accountAccessToken && !isGuestSession) {
    throw new BenefitsAuthenticationError("Benefits session required");
  }

  const request = (token: string | null) => {
    const headers = new Headers(init.headers);
    const guestId = getGuestId();

    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    } else {
      headers.delete("Authorization");
    }

    if (guestId) headers.set("X-Guest-Id", guestId);
    else headers.delete("X-Guest-Id");

    return fetch(input, { ...init, headers });
  };

  let requestToken = benefitsAccessToken || accountAccessToken;
  let response = await request(requestToken);
  storeGuestIdFromHeaders(response.headers);

  if (response.status === 401 && benefitsAccessToken && !hasRefreshed) {
    benefitsAccessToken = await refreshBenefitsAccessToken();
    hasRefreshed = true;
    if (benefitsAccessToken) {
      requestToken = benefitsAccessToken;
      response = await request(requestToken);
      storeGuestIdFromHeaders(response.headers);
    }
  }

  if (
    response.status === 401 &&
    accountAccessToken &&
    requestToken === accountAccessToken &&
    isGuestSession
  ) {
    response = await request(null);
    storeGuestIdFromHeaders(response.headers);
  }

  if ((response.status === 401 || response.status === 403) && !isGuestSession) {
    clearBenefitsSession();
    throw new BenefitsAuthenticationError("Authentication required");
  }

  return response;
};

const asString = (value: unknown) => (typeof value === "string" ? value : "");

const normalizeSummary = (value: any): BenefitsConversationSummary | null => {
  const conversationId = asString(value?.conversationId);
  if (!isBenefitsConversationId(conversationId)) return null;

  return {
    conversationId,
    title: DEFAULT_BENEFITS_CONVERSATION_TITLE,
    createdAt: asString(value?.createdAt),
    updatedAt: asString(value?.updatedAt),
    lastMessage: asString(value?.lastMessage),
  };
};

const normalizeMessage = (value: any, index: number): BenefitsConversationMessage | null => {
  const content = asString(value?.content ?? value?.text ?? value?.message);
  const rawRole = asString(value?.role ?? value?.sender).toLowerCase();
  const role = rawRole === "user"
    ? "user"
    : rawRole === "assistant" || rawRole === "bot"
      ? "assistant"
      : null;
  if (!role || !content) return null;

  return {
    id: asString(value?._id ?? value?.id) || `message-${index}`,
    role,
    content,
    createdAt: asString(value?.createdAt ?? value?.timestamp),
  };
};

export const fetchBenefitsConversations = async ({
  limit = 20,
  cursor,
  signal,
}: {
  limit?: number;
  cursor?: string | null;
  signal?: AbortSignal;
} = {}): Promise<BenefitsConversationPage> => {
  const searchParams = new URLSearchParams({ limit: String(limit) });
  if (cursor) searchParams.set("cursor", cursor);

  const response = await benefitsAuthenticatedFetch(
    `${BENEFITS_CONVERSATIONS}?${searchParams.toString()}`,
    { signal }
  );
  if (!response.ok) throw new Error(`Conversation history failed with status ${response.status}`);

  const payload = unwrapPayload(await response.json());
  const rawConversations = Array.isArray(payload) ? payload : payload?.conversations;
  const conversations = Array.isArray(rawConversations)
    ? rawConversations.map(normalizeSummary).filter(Boolean) as BenefitsConversationSummary[]
    : [];

  return {
    conversations,
    nextCursor: Array.isArray(payload) ? null : asString(payload?.nextCursor) || null,
  };
};

export const fetchBenefitsConversation = async (
  conversationId: string,
  signal?: AbortSignal
): Promise<BenefitsConversationThread> => {
  const response = await benefitsAuthenticatedFetch(
    `${BENEFITS_CONVERSATIONS}/${encodeURIComponent(conversationId)}`,
    { signal }
  );

  if (response.status === 404) {
    throw new BenefitsConversationNotFoundError("Conversation not found");
  }
  if (!response.ok) throw new Error(`Conversation failed with status ${response.status}`);

  const payload = unwrapPayload(await response.json());
  const rawMessages = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.messages)
      ? payload.messages
      : [];

  return {
    conversationId: asString(payload?.conversationId) || conversationId,
    title: DEFAULT_BENEFITS_CONVERSATION_TITLE,
    createdAt: asString(payload?.createdAt),
    updatedAt: asString(payload?.updatedAt),
    messages: rawMessages
      .map(normalizeMessage)
      .filter(Boolean) as BenefitsConversationMessage[],
  };
};
